// A person's compliance records, and the codes behind them (round 27).
import { request } from './api';
import type { ExpiryStatus } from './documentsApi';

export type CompetencyKind = 'licence' | 'ticket' | 'training';
export type RecordCategory = 'competency' | 'induction';

export interface CompetencyCode {
  id: string;
  code: string;
  name: string;
  kind: CompetencyKind;
  description: string | null;
  /** Null means it does not expire. A pre-fill only — never computed server-side. */
  validityMonths: number | null;
  sortOrder: number;
  archived: boolean;
}

/** A standard induction (round 37) — a name, no short code, no kind. */
export interface InductionCode {
  id: string;
  name: string;
  description: string | null;
  /** Null means it does not expire. A pre-fill only — never computed server-side. */
  validityMonths: number | null;
  sortOrder: number;
  archived: boolean;
}

export interface PersonRecord {
  id: string;
  resourceId: string;
  category: RecordCategory;
  /** Its code's name — either list, since round 37. */
  title: string;
  code: string | null;
  /** The competency code id; null on an induction. */
  codeId: string | null;
  /** The induction code id; null on a competency. */
  inductionCodeId: string | null;
  codeKind: CompetencyKind | null;
  /** True when this record's code — whichever list — has been retired. */
  codeArchived: boolean;
  recordNumber: string | null;
  issueDate: string | null;
  expiresOn: string | null;
  /** The fixed 30-day chip, NOT the company's reminder window (spec 03). */
  expiry: ExpiryStatus;
  notes: string | null;
  createdByName: string;
  createdAt: string;
  isSampleData: boolean;
}

export interface RecordInput {
  category: RecordCategory;
  competencyCodeId?: string | null;
  inductionCodeId?: string | null;
  recordNumber?: string | null;
  issueDate?: string | null;
  expiryDate?: string | null;
  notes?: string | null;
}

export const KIND_LABEL: Record<CompetencyKind, string> = {
  licence: 'Licence',
  ticket: 'Ticket',
  training: 'Training',
};

/** "2 years" / "18 months" / "No expiry" — years where it divides cleanly. */
export function validityLabel(months: number | null): string {
  if (months === null) return 'No expiry';
  if (months % 12 === 0) {
    const years = months / 12;
    return `${years} year${years === 1 ? '' : 's'}`;
  }
  return `${months} months`;
}

/** "HR — Driver licence — heavy rigid", the way it reads in a picker. */
export function codeLabel(code: CompetencyCode): string {
  return `${code.code} · ${code.name}`;
}

/**
 * Which codes to offer, given what this record already holds.
 *
 * Active ones, plus the currently-held one even when it has since been
 * archived — labelled, so somebody editing an old record can see why it is
 * there and still save without being forced to change it.
 */
export function codeOptions(codes: CompetencyCode[], currentId: string | null) {
  return codes
    .filter((c) => !c.archived || c.id === currentId)
    .map((c) => ({
      value: c.id,
      label: c.archived ? `${codeLabel(c)} (archived)` : codeLabel(c),
    }));
}

/** The induction picker's options — codeOptions' rule, name-only labels. */
export function inductionOptions(codes: InductionCode[], currentId: string | null) {
  return codes
    .filter((c) => !c.archived || c.id === currentId)
    .map((c) => ({
      value: c.id,
      label: c.archived ? `${c.name} (archived)` : c.name,
    }));
}

/**
 * The expiry date a code's validity suggests, from an issue date.
 *
 * CLIENT-SIDE ONLY, and only ever a suggestion: the server never computes an
 * expiry, because a ticket issued under an older validity keeps the date
 * printed on the card. Month arithmetic is allowed to overflow the way
 * JavaScript does it (31 Aug + 6 months lands in March) rather than being
 * clamped — the person can see the box and change it.
 *
 * Years below 1900 are refused, and not as pedantry: a date input fires
 * change with a COMPLETE value while the year is still being typed ('0002…',
 * '0020…', '0202…'), and `Date.UTC` maps years 0–99 to 1900+y besides — the
 * pair of which once turned a half-typed 2026 into an expiry in the 1930s.
 * `setUTCFullYear` is used instead of the `Date.UTC` constructor because it
 * is immune to that two-digit mapping.
 */
export function prefillExpiry(issueDate: string, validityMonths: number | null): string {
  if (!issueDate || validityMonths === null) return '';
  const [y, m, d] = issueDate.split('-').map(Number);
  if (!y || !m || !d || y < 1900 || y > 9999) return '';
  const shifted = new Date(0);
  shifted.setUTCFullYear(y, m - 1 + validityMonths, d);
  return shifted.toISOString().slice(0, 10);
}

export const people = {
  records: (resourceId: string) =>
    request<{ records: PersonRecord[] }>(`/resources/${resourceId}/records`),

  addRecord: (resourceId: string, body: RecordInput) =>
    request<{ record: PersonRecord }>(`/resources/${resourceId}/records`, {
      method: 'POST',
      body,
    }),

  updateRecord: (resourceId: string, recordId: string, body: Partial<RecordInput>) =>
    request<{ record: PersonRecord }>(`/resources/${resourceId}/records/${recordId}`, {
      method: 'PATCH',
      body,
    }),

  removeRecord: (resourceId: string, recordId: string) =>
    request<void>(`/resources/${resourceId}/records/${recordId}`, { method: 'DELETE' }),

  /** My own record — every role, read-only, no setting gates it (round 27). */
  myRecord: () =>
    request<{
      resource: {
        id: string;
        name: string;
        /** Round 37, where the office recorded them. Null otherwise. */
        dateOfBirth: string | null;
        employmentStartDate: string | null;
      } | null;
      records: PersonRecord[];
    }>('/my/record'),
};

export const competencyCodes = {
  list: (options: { includeArchived?: boolean } = {}) =>
    request<{ codes: CompetencyCode[] }>(
      `/competency-codes${options.includeArchived ? '?archived=true' : ''}`
    ),

  create: (body: {
    code: string;
    name: string;
    kind: CompetencyKind;
    description?: string | null;
    validityMonths?: number | null;
  }) => request<{ code: CompetencyCode }>('/competency-codes', { method: 'POST', body }),

  update: (
    id: string,
    body: Partial<{
      code: string;
      name: string;
      kind: CompetencyKind;
      description: string | null;
      validityMonths: number | null;
    }>
  ) => request<{ code: CompetencyCode }>(`/competency-codes/${id}`, { method: 'PATCH', body }),

  archive: (id: string, archived: boolean) =>
    request<{ code: CompetencyCode }>(`/competency-codes/${id}/archive`, {
      method: 'POST',
      body: { archived },
    }),

  remove: (id: string) => request<void>(`/competency-codes/${id}`, { method: 'DELETE' }),
};

export const inductionCodes = {
  list: (options: { includeArchived?: boolean } = {}) =>
    request<{ codes: InductionCode[] }>(
      `/induction-codes${options.includeArchived ? '?archived=true' : ''}`
    ),

  create: (body: { name: string; description?: string | null; validityMonths?: number | null }) =>
    request<{ code: InductionCode }>('/induction-codes', { method: 'POST', body }),

  update: (
    id: string,
    body: Partial<{ name: string; description: string | null; validityMonths: number | null }>
  ) => request<{ code: InductionCode }>(`/induction-codes/${id}`, { method: 'PATCH', body }),

  archive: (id: string, archived: boolean) =>
    request<{ code: InductionCode }>(`/induction-codes/${id}/archive`, {
      method: 'POST',
      body: { archived },
    }),

  remove: (id: string) => request<void>(`/induction-codes/${id}`, { method: 'DELETE' }),
};
